import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { FieldStatusDto, SourcingEventV2Dto } from "../../../../../../autogen/bff-api";
import type {
  ReadinessLookup,
  KnownFieldKey,
} from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import {
  environmentRuleApplies,
  isEnvironmentCriterionType,
  isExemption,
  MIN_ENVIRONMENT_WEIGHTING_PERCENT,
} from "../../utils/publicProcurementOptions";
import { AwardCriteriaWeightsTable } from "./AwardCriteriaWeightsTable";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummarySection } from "./SummarySection";
import { useProcurementTerms } from "../../../../../../common/terminology/useProcurementTerms";

export const FIELD_KEYS = [
  "AwardCriterionModelSet",
  "AwardCriteriaWeightsValid",
  "AwardSubCriteriaWeightsValid",
  "AwardCriteriaDescriptionsPresent",
  "AwardSubCriteriaDescribed",
  "AwardPriceCriterionUnique",
  "EnvironmentCriterionWeightingJustified",
] as const satisfies readonly KnownFieldKey[];

const LOWEST_PRICE_IRRELEVANT: ReadonlySet<KnownFieldKey> = new Set([
  "AwardCriteriaWeightsValid",
  "AwardSubCriteriaWeightsValid",
  "AwardCriteriaDescriptionsPresent",
  "AwardSubCriteriaDescribed",
  "AwardPriceCriterionUnique",
  "EnvironmentCriterionWeightingJustified",
]);

interface AwardCriteriaSectionProps {
  event: SourcingEventV2Dto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const AwardCriteriaSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
}: AwardCriteriaSectionProps) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const awardCriteria = event.eventData.awardCriteria;
  const isLowestPrice = awardCriteria?.lowestPrice != null;
  const criteria = awardCriteria?.bestPriceQualityRatio?.criteria ?? [];
  const environmentJustification =
    awardCriteria?.bestPriceQualityRatio?.environmentWeightingJustification?.trim() ?? "";

  // § 5b/§ 5c: surface the justification only while it is relevant — the rule applies (Del II/III) AND
  // the summed environment weighting is still below the 30 % floor. This mirrors the step's
  // needsEnvironmentJustification, so a justification that is merely preserved (kept on the draft at
  // >= 30 % so a transient bump doesn't lose it, or left over from a since-changed part) is never shown.
  const environmentWeight = criteria
    .filter((c) => isEnvironmentCriterionType(c.type))
    .reduce((sum, c) => sum + (c.weight?.number ?? 0), 0);
  const environmentStatus = readinessFor("EnvironmentCriterionWeightingJustified");
  const showEnvironmentJustification =
    environmentRuleApplies(event.eventContext.type === "Public" ? event.eventContext.foaPart : undefined) &&
    environmentWeight < MIN_ENVIRONMENT_WEIGHTING_PERCENT &&
    !isLowestPrice;

  const fieldStatuses = FIELD_KEYS.filter(
    (k) => !(isLowestPrice && LOWEST_PRICE_IRRELEVANT.has(k)),
  )
    .map(readinessFor)
    .filter((s): s is FieldStatusDto => s !== undefined);

  const modelLabel = isLowestPrice
    ? t("Lowest price")
    : awardCriteria?.bestPriceQualityRatio
      ? t("Best price-quality ratio")
      : null;

  return (
    <SummarySection
      title={terms.awardCriteria}
      editTarget={{ kind: "wizard-step", step: "awardCriteria" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryField
        label={terms.awardModel}
        value={modelLabel ?? <EmptyValue text={t("Not set")} />}
        status={readinessFor("AwardCriterionModelSet")}
      />
      {!isLowestPrice && criteria.length > 0 && (
        <Box mt="3">
          {/* The weight rules have no field of their own, so they hang off the table they describe —
              otherwise Publish disables with nothing on this page to point at. */}
          <SummaryField
            label={t("Criteria & weights")}
            value={<AwardCriteriaWeightsTable criteria={criteria} />}
            status={readinessFor("AwardCriteriaWeightsValid")}
            extraStatuses={[
              readinessFor("AwardSubCriteriaWeightsValid"),
              readinessFor("AwardSubCriteriaDescribed"),
              readinessFor("AwardCriteriaDescriptionsPresent"),
              readinessFor("AwardPriceCriterionUnique"),
            ]}
          />
        </Box>
      )}
      {showEnvironmentJustification && (
        <Box mt="3">
          <SummaryField
            label={
              isExemption(criteria)
                ? t("Justification for the exemption")
                : t("Justification for the lower weighting")
            }
            value={
              environmentJustification !== "" ? (
                <Text whiteSpace="pre-wrap">{environmentJustification}</Text>
              ) : (
                <EmptyValue text={t("Not added")} />
              )
            }
            status={environmentStatus}
          />
        </Box>
      )}
    </SummarySection>
  );
};
